import { observer } from 'mobx-react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import _ from 'lodash'
import {
  Block,
  ExternalLink,
  Heading,
  Paragraph,
  SingleSelect,
  SingleSelectData,
} from 'suomifi-ui-components'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import {
  getDisplayValueForReligiousCommunities,
  getDisplayValueForKansalaisuus,
} from 'todistukset-common/src/vtj/localization.util'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { isHetuIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import InfoItem from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/InfoItem.react'
import {
  MAX_NUMBER_OF_MARRIAGES,
  validateCountryOfResidenceCode,
  validateMaritalStatus,
  validateNumberOfMarriages,
} from 'todistukset-common/src/vtj/validation'
import {
  FormContainer,
  InfoInlineAlert,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type {
  ForwardRefWrappedComponent,
  StepComponentProps,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useCountrySelectData } from 'todistukset-common/src/vtj/ui/country-select.util'
import { useAriaOptionsFunction } from 'todistukset-ui/src/vtj/components/order-wizard/country-select.util'
import {
  FirstErrorImperativeHandle,
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useAetOrdererFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-orderer-form-actions'
import { useAetSignerFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-signer-form-actions'
import { useAetOrdererForm } from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { maritalStatusBlocksAet } from 'todistukset-common/src/vtj/MaritalStatus.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  AetOrdererUiForm,
  AetSignerUiForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { numberOfMarriagesSelectItems } from 'todistukset-common/src/vtj/form-data.util'
import { MaritalStatusSelect } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/MaritalStatusSelect.react'

interface AetOwnInfoProps
  extends Pick<StepComponentProps, 'titleId' | 'orderFlowName'> {
  useForm: typeof useAetOrdererForm | typeof useAetSignerForm
  useFormActions:
    | typeof useAetOrdererFormActions
    | typeof useAetSignerFormActions
}

type AetOwnInfoComponent = ForwardRefWrappedComponent<
  FirstErrorImperativeHandle,
  AetOwnInfoProps
>

type AetOwnInfoSubComponent = ForwardRefWrappedComponent<
  FirstErrorImperativeHandle,
  Pick<AetOwnInfoProps, 'orderFlowName' | 'useForm' | 'useFormActions'>
>

const AetOwnInfo: AetOwnInfoComponent = observer(
  forwardRef(function AetOwnInfo(
    { titleId, useForm, useFormActions, orderFlowName },
    ref
  ) {
    const { t } = useTranslation()
    const {
      common: { lang, loggedInUser },
    } = useStore()

    const {
      orderer: {
        maritalStatus,
        numberOfMarriages,
        citizenshipCode,
        religiousCommunities,
      },
    } = useForm()

    const numberOfMarriagesValue = finalEditableFieldValue(numberOfMarriages)
    const maritalStatusValue = finalEditableFieldValue(maritalStatus)

    const maritalStatusVTJ = maritalStatus?.vtjArvo ?? null
    const numberOfMarriagesVTJ = numberOfMarriages?.vtjArvo ?? null
    const hasMaritalStatusVTJ =
      ![null, MaritalStatus.EI_TIETOA].includes(maritalStatusVTJ) &&
      numberOfMarriagesVTJ !== null

    const isInRegisteredPartnership =
      maritalStatusValue === MaritalStatus.REKISTEROIDYSSA_PARISUHTEESSA
    const isMarried = maritalStatusBlocksAet(
      maritalStatusValue ?? MaritalStatus.EI_TIETOA
    )

    const user = loggedInUser.get()
    if (!isHetuIdentifiedUser(user)) {
      // This is for satisfying typescript, it should be checked long before we get here.
      throw new Error('AET user needs hetu')
    }

    const displayedCitizenshipInfo = !_.isEmpty(
      finalEditableFieldValue(citizenshipCode)
    )
      ? getDisplayValueForKansalaisuus(citizenshipCode, lang)
      : t('aetOwnInfoNoInformation')

    const maritalStatusErrorRef = useRef<FirstErrorImperativeHandle>(null)
    const countryOfResidenceErrorRef = useRef<FirstErrorImperativeHandle>(null)
    useImperativeHandle(
      ref,
      () => ({
        getFirstErrorField: () =>
          maritalStatusErrorRef.current?.getFirstErrorField() ??
          countryOfResidenceErrorRef.current?.getFirstErrorField() ??
          null,
      }),
      []
    )

    const displayedReligiousCommunityInfo = Array.isArray(religiousCommunities)
      ? getDisplayValueForReligiousCommunities(religiousCommunities, lang)
      : t('aetOwnInfoNoInformation')

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetOwnInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOwnInfoMainDescription_paragraphs" />
          <Paragraph>
            <ExternalLink
              href={t('aetOwnInfoUpdateVtjLinkHref')}
              labelNewWindow={t('opensInNewWindow')}
              underline="initial"
            >
              <LocalizedString stringKey="aetOwnInfoUpdateVtjLinkText" />
            </ExternalLink>
          </Paragraph>
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOwnInfoItemsTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOwnInfoItemsDescription_paragraphs" />
          <Columns data-test-id="info-items">
            <InfoItem
              title={t('aetOwnInfoNameLabel')}
              content={`${user.lastName}, ${user.firstNames}`}
            />
            <InfoItem
              title={t('aetOwnInfoPersonIdLabel')}
              content={`${user.hetu}`}
            />
            <InfoItem
              title={t('aetOwnInfoCitizenshipLabel')}
              content={displayedCitizenshipInfo}
              testIdPrefix="citizenship"
            />
            <InfoItem
              title={t('aetOwnInfoReligiousCommunityLabel')}
              content={displayedReligiousCommunityInfo}
              testIdPrefix="religious-community"
            />
            {hasMaritalStatusVTJ && (
              <InfoItem
                title={t('aetOwnInfoMaritalStatusLabel')}
                content={t(`maritalStatus_${maritalStatusValue}`)}
                testIdPrefix="maritalStatus"
              />
            )}
            {hasMaritalStatusVTJ && (
              <InfoItem
                title={t('aetOwnInfoNumberOfMarriagesLabel')}
                content={String(numberOfMarriagesValue)}
                testIdPrefix="numberOfMarriages"
              />
            )}
          </Columns>
        </Block>
        {!hasMaritalStatusVTJ && (
          <Block variant="section">
            <Heading variant="h3">
              <LocalizedString stringKey="aetOwnInfoMaritalStatusTitle" />
            </Heading>
            <LocalizedParagraphs stringKey="aetOwnInfoMaritalStatusDescription_paragraphs" />
            <OrdererInfoMaritalStatusFields
              useForm={useForm}
              useFormActions={useFormActions}
              orderFlowName={orderFlowName}
            />
            <InfoInlineAlert status="warning" ariaLiveMode="off" mb="l">
              <LocalizedParagraphs stringKey="aetOwnInfoSelfProvidedMarriageStatus_paragraphs" />
            </InfoInlineAlert>
          </Block>
        )}
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOwnInfoCountryOfResidenceTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOwnInfoCountryOfResidenceDescription_paragraphs" />
          <OrdererInfoCountryOfResidence
            orderFlowName={orderFlowName}
            useForm={useForm}
            useFormActions={useFormActions}
          />
          {isInRegisteredPartnership ? (
            <InfoInlineAlert status="error">
              <LocalizedString stringKey="aetOwnInfoCannotOrderAetTodistusBecauseYouAreInRegisteredPartnership" />
            </InfoInlineAlert>
          ) : (
            isMarried && (
              <InfoInlineAlert status="error">
                <LocalizedString stringKey="aetOwnInfoCannotOrderAetTodistusBecauseYouAreMarried" />
              </InfoInlineAlert>
            )
          )}
        </Block>
      </FormContainer>
    )
  })
)

const OrdererInfoMaritalStatusFields: AetOwnInfoSubComponent = observer(
  forwardRef(function OrdererInfoMaritalStatusFields(
    { useForm, useFormActions, orderFlowName },
    ref
  ) {
    const { t } = useTranslation()
    const {
      common: { loggedInUser },
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()
    const form = useForm()
    const user = loggedInUser.get()
    const { setMaritalStatus, setNumberOfMarriages } = useFormActions()
    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getMaritalStatusErrors,
      ['maritalStatus', 'numberOfMarriages']
    )

    const ariaOptionsFunction = useAriaOptionsFunction()

    const [
      appendNumberOfMarriagesSelectItems,
      setAppendNumberOfMarriagesSelectItems,
    ] = useState(numberOfMarriagesSelectItems)

    const numberOfMarriagesSelectedItem =
      appendNumberOfMarriagesSelectItems.find(
        (item) =>
          item.uniqueItemId ===
          finalEditableFieldValue(form.orderer.numberOfMarriages)?.toString()
      ) || null

    const handleNumberOfMarriagesSelectionChange = (
      newSelectedItem: SingleSelectData | null
    ) => {
      if (newSelectedItem !== null) {
        const isNewValue = !numberOfMarriagesSelectItems.some(
          (item) => item.uniqueItemId === newSelectedItem.uniqueItemId
        )
        if (isNewValue) {
          setAppendNumberOfMarriagesSelectItems(
            numberOfMarriagesSelectItems.concat([newSelectedItem])
          )
        } else {
          setAppendNumberOfMarriagesSelectItems(numberOfMarriagesSelectItems)
        }
      } else {
        setAppendNumberOfMarriagesSelectItems(numberOfMarriagesSelectItems)
      }
      setNumberOfMarriages(newSelectedItem)
    }

    return (
      <>
        <Columns>
          <MaritalStatusSelect
            maritalStatus={form.orderer.maritalStatus}
            setMaritalStatus={setMaritalStatus}
            maritalStatusRef={refs.maritalStatus}
            errorStatus={errorStatus({
              field: 'maritalStatus',
              user,
              form,
              showErrors,
              t,
            })}
          />
          <SingleSelect
            id="numberOfMarriages"
            data-test-id="number-of-marriages"
            labelText={t('aetOwnInfoNumberOfMarriagesLabel')}
            visualPlaceholder=""
            items={appendNumberOfMarriagesSelectItems}
            clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
            ariaOptionsAvailableTextFunction={ariaOptionsFunction}
            selectedItem={numberOfMarriagesSelectedItem}
            onItemSelectionChange={handleNumberOfMarriagesSelectionChange}
            {...errorStatus({
              field: 'numberOfMarriages',
              user,
              form,
              showErrors,
              t,
            })}
            forwardedRef={refs.numberOfMarriages}
            disabled={
              finalEditableFieldValue(form.orderer.maritalStatus) ===
                MaritalStatus.NAIMATON ||
              finalEditableFieldValue(form.orderer.maritalStatus) === null
            }
            allowItemAddition
            itemAdditionHelpText={t(
              'validationNumberOfMarriagesExceedsMaximum',
              { maxCount: MAX_NUMBER_OF_MARRIAGES }
            )}
          />
        </Columns>
      </>
    )
  })
)

const OrdererInfoCountryOfResidence: AetOwnInfoSubComponent = observer(
  forwardRef(function OrdererInfoCountryOfResidence(
    { orderFlowName, useForm, useFormActions },
    ref
  ) {
    const { t } = useTranslation()
    const {
      common: { loggedInUser, lang },
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const user = loggedInUser.get()
    const form = useForm()
    const { setCountryOfResidence } = useFormActions()
    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getCountryOfResidenceErrors,
      ['countryOfResidenceCode']
    )
    const { countrySelectList, countrySelectIndex } = useCountrySelectData({
      lang,
    })
    const ariaOptionsFunction = useAriaOptionsFunction()
    const countryOfResidenceCode = form.orderer.countryOfResidenceCode
    const currentCountryOfResidenceCode = finalEditableFieldValue(
      countryOfResidenceCode
    )
    const livesInFinland = currentCountryOfResidenceCode === EuCountries.FINLAND

    return (
      <>
        <Columns>
          <SingleSelect
            id="select-country-widget"
            data-test-id="single-select-country"
            labelText={t('aetOwnInfoCountryOfResidenceLabel')}
            visualPlaceholder={t('aetOwnInfoCountryOfResidencePrompt')}
            items={countrySelectList}
            clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
            noItemsText={t('orderWizardSingleSelectNoMatchingItems')}
            ariaOptionsAvailableTextFunction={ariaOptionsFunction}
            selectedItem={
              currentCountryOfResidenceCode
                ? countrySelectIndex[currentCountryOfResidenceCode]
                : null
            }
            onItemSelectionChange={setCountryOfResidence}
            {...errorStatus({
              field: 'countryOfResidenceCode',
              user,
              form,
              showErrors,
              t,
            })}
            forwardedRef={refs.countryOfResidenceCode}
          />
        </Columns>
        {!livesInFinland && (
          <InfoInlineAlert aria-live="polite" mb="xxl">
            <Heading variant="h4">
              <LocalizedString stringKey="aetOwnInfoLivesAboardTitle_1" />
            </Heading>
            <LocalizedParagraphs stringKey="aetOwnInfoLivesAboardText_1_paragraphs" />
            <Heading variant="h5">
              <LocalizedString stringKey="aetOwnInfoLivesAboardTitle_2" />
            </Heading>
            <LocalizedParagraphs stringKey="aetOwnInfoLivesAboardText_2_paragraphs" />
            <Heading variant="h5">
              <LocalizedString stringKey="aetOwnInfoLivesAboardTitle_3" />
            </Heading>
            <LocalizedParagraphs stringKey="aetOwnInfoLivesAboardText_3_paragraphs" />
            <Paragraph>
              <ExternalLink
                href={t('aetOwnInfoLivesAbroadLinkHref')}
                labelNewWindow={t('opensInNewWindow')}
                underline="initial"
              >
                <LocalizedString stringKey="aetOwnInfoLivesAbroadLinkText" />
              </ExternalLink>
            </Paragraph>
          </InfoInlineAlert>
        )}
      </>
    )
  })
)

export default AetOwnInfo

function errorStatus(params: {
  field:
    | keyof ReturnType<typeof getMaritalStatusErrors>
    | keyof ReturnType<typeof getCountryOfResidenceErrors>
  user: TodistuksetUser
  form: AetOrdererUiForm | AetSignerUiForm
  showErrors: boolean
  t: TFunction
}) {
  return getErrorStatus({
    ...params,
    errors: {
      ...getMaritalStatusErrors(params.form, params.user),
      ...getCountryOfResidenceErrors(params.form, params.user),
    },
  })
}

function getCountryOfResidenceErrors(
  form: AetOrdererUiForm | AetSignerUiForm,
  user: TodistuksetUser
) {
  return {
    countryOfResidenceCode: validateCountryOfResidenceCode(
      finalEditableFieldValue(form.orderer.countryOfResidenceCode),
      form,
      user
    ),
  }
}

function getMaritalStatusErrors(
  form: AetOrdererUiForm | AetSignerUiForm,
  user: TodistuksetUser
) {
  return {
    maritalStatus: validateMaritalStatus(
      finalEditableFieldValue(form.orderer.maritalStatus),
      form,
      user
    ),
    numberOfMarriages: validateNumberOfMarriages(
      form.orderer.numberOfMarriages,
      form
    ),
  }
}
